import React from "react";
import { ErrorMessage, Field } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import { AddressFormSection, AddressLookupProvider, AddressPanel } from "../../UI";
import { useViewport } from "../../../contexts";

const COMPANY_ADDRESS_REQUIRED = ["addressLine1", "town", "postcode", "country"] as const;

function GridFieldCell({ children }: { children: React.ReactNode }) {
  return <div className="mb-3">{children}</div>;
}

function CompanyPhoneField() {
  return (
    <Form.Group>
      <Form.Label>
        Phone <span className="text-danger">*</span>
      </Form.Label>
      <Field name="phone" type="tel" className="form-control" />
      <ErrorMessage name="phone" component="div" className="invalid-feedback d-block" />
    </Form.Group>
  );
}

function CompanyContactEmailField() {
  return (
    <Form.Group>
      <Form.Label>
        Contact Email <span className="text-danger">*</span>
      </Form.Label>
      <Field name="contactEmail" type="email" className="form-control" />
      <ErrorMessage name="contactEmail" component="div" className="invalid-feedback d-block" />
    </Form.Group>
  );
}

function CompanyWebsiteField() {
  return (
    <Form.Group>
      <Form.Label>Website</Form.Label>
      <Field name="website" type="url" className="form-control" placeholder="https://example.com" />
      <ErrorMessage name="website" component="div" className="invalid-feedback d-block" />
    </Form.Group>
  );
}

function CompanyInvitationEmailField() {
  return (
    <Form.Group>
      <Form.Label>
        Invitation Email <span className="text-danger">*</span>
      </Form.Label>
      <Field name="invitationEmail" type="email" className="form-control" />
      <ErrorMessage name="invitationEmail" component="div" className="invalid-feedback d-block" />
    </Form.Group>
  );
}

function CompanyAddressPanel({
  layoutVariant,
  postcodeLayout,
}: {
  layoutVariant: "pairedRows" | "stacked";
  postcodeLayout: "below" | "separate";
}) {
  return (
    <AddressPanel className="mb-3">
      <AddressFormSection
        layoutVariant={layoutVariant}
        postcodeLayout={postcodeLayout}
        requiredFields={[...COMPANY_ADDRESS_REQUIRED]}
      />
    </AddressPanel>
  );
}

function CompanyInfoFormGridLarge() {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col lg={4}>
          <div className="vstack gap-2">
            <GridFieldCell>
              <CompanyPhoneField />
            </GridFieldCell>
            <GridFieldCell>
              <CompanyContactEmailField />
            </GridFieldCell>
            <GridFieldCell>
              <CompanyWebsiteField />
            </GridFieldCell>
            <GridFieldCell>
              <CompanyInvitationEmailField />
            </GridFieldCell>
          </div>
        </Col>
        <Col lg={8}>
          <CompanyAddressPanel layoutVariant="pairedRows" postcodeLayout="below" />
        </Col>
      </Row>
    </div>
  );
}

function CompanyInfoFormGridMedium() {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col md={6}>
          <div className="vstack gap-2">
            <GridFieldCell>
              <CompanyPhoneField />
            </GridFieldCell>
            <GridFieldCell>
              <CompanyContactEmailField />
            </GridFieldCell>
            <GridFieldCell>
              <CompanyWebsiteField />
            </GridFieldCell>
            <GridFieldCell>
              <CompanyInvitationEmailField />
            </GridFieldCell>
          </div>
        </Col>
        <Col md={6}>
          <CompanyAddressPanel layoutVariant="stacked" postcodeLayout="below" />
        </Col>
      </Row>
    </div>
  );
}

function CompanyInfoFormGridNarrow() {
  return (
    <>
      <div className="mb-3">
        <CompanyPhoneField />
      </div>
      <div className="mb-3">
        <CompanyContactEmailField />
      </div>
      <CompanyAddressPanel layoutVariant="stacked" postcodeLayout="separate" />
      <div className="mb-3">
        <CompanyWebsiteField />
      </div>
      <div className="mb-3">
        <CompanyInvitationEmailField />
      </div>
    </>
  );
}

export function CompanyInfoFormGrid() {
  const { width } = useViewport();

  return (
    <AddressLookupProvider>
      {width >= 992 ? (
        <CompanyInfoFormGridLarge />
      ) : width >= 768 ? (
        <CompanyInfoFormGridMedium />
      ) : (
        <CompanyInfoFormGridNarrow />
      )}
    </AddressLookupProvider>
  );
}
